import { CART } from "./../../const/cookies.mjs";
import { resolveCartOptions, resolveCartRouteOptions } from "./utils.mjs";
import { useStoredCart } from "./storage.mjs";
import { useResetCart } from "./clear-cart.mjs";
import { useExceptions } from "../exceptions.mjs";
import { useAuth } from "../auth/index.mjs";
import { useSsrCoreApp } from "../ssrControl.mjs";
import { sleep } from "../../utils/async.mjs";
import { extractValue } from "../../utils/collections.mjs";
import { useAlerts } from "../alerts.mjs";
import { toNonNullable } from "../../utils/collections.mjs";
import {
  AddConfigurableItemDocument,
  AddCustomOptionsToCartItemsDocument,
  AddItemDocument,
  AddNoteToCartDocument,
  ApplyCouponToCartDocument,
  ApplyRewardPointsToCartDocument,
  CreateCartDocument,
  GuestPaymentMethodsDocument,
  PaymentMethodsDocument,
  RemoveCartItemsCustomOptionsDocument,
  RemoveCouponFromCartDocument,
  RemoveItemDocument,
  RemoveRewardPointsFromCartDocument,
  SetBillingAddressDocument,
  SetFramelessPaymentMethodOnCartDocument,
  SetGuestEmailOnCartDocument,
  SetOrderSourceOnCartDocument,
  SetPaymentMethodOnCartDocument,
  SetShippingAddressDocument,
  SetShippingMethodDocument,
  SetWalletPaymentMethodDocument,
  UpdateCartItemsCustomOptionsDocument,
  UpdateItemDocument
} from "../../graphql/Cart.mjs";
import { SetShippingLocationOnCartDocument } from "../../graphql/StorePicker.mjs";
import { useI18n } from "vue-i18n";
import { isNumber, uniqBy } from "lodash-es";
import { useMutation, useQuery } from "villus";
import { computed, onMounted, ref, toRefs, watch } from "vue";
import { useCookie, useRoute, useRuntimeConfig } from "#app";
export function useAddCartItem() {
  const {
    execute: executeAddingSimpleProduct,
    isFetching: isFetchingSimplePrduct
  } = useMutation(AddItemDocument);
  const {
    execute: executeAddingConfigurableProduct,
    isFetching: isFetchingConfigurableProduct
  } = useMutation(AddConfigurableItemDocument);
  const { cartState, mapCartState, patchCartState } = useStoredCart();
  const { prepareCart } = usePrepareCart();
  const { resetCart } = useResetCart();
  const { cartError } = useCartErrors();
  async function addItem(item) {
    await prepareCart();
    const items = Array.isArray(item) ? item : [item];
    const result = await executeAddingSimpleProduct({
      cartId: cartState.value.cartId,
      items,
      ...resolveCartOptions("addCartItem")
    });
    if (result.error && (/Current user does not have an active cart\./i.test(
      result.error.message
    ) || /The current user cannot perform operations on cart "(.*)"/gm.test(
      result.error.message
    ) || /Could not find a cart with ID "(.*)"/gm.test(result.error.message) || /The cart isn't active\./i.test(result.error.message))) {
      resetCart();
      await addItem(item);
      return;
    }
    if (result.error) {
      throw new Error(result.error.message);
    }
    if (result.data?.response) {
      if (result.data?.response?.user_errors?.length > 0) {
        result.data?.response?.user_errors.forEach((error) => {
          cartError({ code: error?.code, message: error?.message });
        });
        throw new Error("Cart error");
      }
      patchCartState(mapCartState(result.data.response.cart));
      for (let i = 0; i < items.length; i++) {
        const cartItem = getCartItem(items[i].sku);
        if (!cartItem) {
          return;
        }
      }
    }
  }
  async function addConfigurableProductItem(item) {
    await prepareCart();
    const items = Array.isArray(item) ? item : [item];
    const result = await executeAddingConfigurableProduct({
      cartId: cartState.value.cartId,
      items,
      ...resolveCartOptions("addCartItem")
    });
    if (result.error && (/Current user does not have an active cart\./i.test(
      result.error.message
    ) || /The current user cannot perform operations on cart "(.*)"/gm.test(
      result.error.message
    ) || /Could not find a cart with ID "(.*)"/gm.test(result.error.message) || /The cart isn't active\./i.test(result.error.message))) {
      resetCart();
      await addConfigurableProductItem(item);
      return;
    }
    if (result.error) {
      throw new Error(result.error.message);
    }
    if (result.data?.response) {
      patchCartState(mapCartState(result.data.response.cart));
      for (let i = 0; i < items.length; i++) {
        const cartItem = getCartItem(items[i].data.sku);
        if (!cartItem) {
          return;
        }
      }
    }
  }
  async function add(items) {
    const input = (Array.isArray(items) ? items : [items]).filter(
      (item) => !!item.quantity
    );
    if (!input.length) return;
    if (input?.[0]?.parentSku) {
      await addConfigurableProductItem(
        input.map((i) => {
          return {
            data: {
              quantity: i.quantity,
              sku: i.sku,
              parent_sku: i.parentSku
            },
            parent_sku: i.parentSku,
            customizable_options: i.customizable_options
          };
        })
      );
      return;
    }
    if (input?.[0]?.bundle_options?.length) {
      await addItem(
        input.map((i) => {
          return {
            quantity: i.quantity,
            sku: i.sku,
            selected_options: i.bundle_options?.map((option) => option.value?.[0]) || []
          };
        })
      );
      return;
    }
    await addItem(
      input.map((i) => {
        return {
          quantity: i.quantity,
          sku: i.sku,
          entered_options: i.entered_options
        };
      })
    );
  }
  return {
    addItem,
    addConfigurableProductItem,
    add,
    isFetching: computed(
      () => isFetchingConfigurableProduct.value || isFetchingSimplePrduct.value
    )
  };
}
export function useUpdateCartItem(id) {
  const { execute, isFetching } = useMutation(UpdateItemDocument);
  const { cartState, patchCartState, mapCartState } = useStoredCart();
  const route = useRoute();
  async function updateItem(quantity) {
    const idValue = id.value ? id.value : id;
    const item = cartState.value.items.find((i) => idValue === i.id);
    if (!item) {
      if (process.env.NODE_ENV !== "production") {
        console.warn(`Could not find product with ${idValue} in cart`);
      }
      return;
    }
    const { data, error } = await execute({
      input: {
        cart_id: cartState.value.cartId,
        cart_items: [
          {
            cart_item_uid: id.value ? id.value : id,
            quantity
          }
        ]
      },
      ...resolveCartOptions("updateCartItem"),
      ...resolveCartRouteOptions("updateCartItem", route.name)
    });
    if (data?.response?.cart) {
      patchCartState(mapCartState(data?.response.cart));
    }
    if (error) {
      throw error;
    }
  }
  return {
    updateItem,
    isFetching
  };
}
export function useRemoveCartItem(uid) {
  const { execute, isFetching: isRemoving } = useMutation(RemoveItemDocument);
  const route = useRoute();
  const { cartState, patchCartState, mapCartState } = useStoredCart();
  async function removeItem() {
    const item = cartState.value.items.find((i) => String(i.id) === uid);
    if (!item) {
      if (process.env.NODE_ENV !== "production") {
        console.warn(`Could not find product with ${uid} in cart`);
      }
    }
    const result = await execute({
      input: {
        cart_id: cartState.value.cartId,
        cart_item_uid: uid
      },
      ...resolveCartOptions("removeCartItem"),
      ...resolveCartRouteOptions("removeCartItem", route.name)
    });
    if (result?.data?.response?.cart) {
      patchCartState(mapCartState(result.data.response.cart));
    }
    if (result.error) {
      throw new Error(result.error.message);
    }
  }
  return { removeItem, isRemoving };
}
export function useUpdateProductStepItem(sku, parentSku, bundleOptions, options) {
  const { cartItemCount, itemId } = useCartItemCount(sku);
  const { updateItem } = useUpdateCartItem(itemId);
  const { add } = useAddCartItem();
  const { warn } = useAlerts();
  const { t } = useI18n();
  const isUpdatingQuantity = ref(false);
  async function updateCartQty(qty) {
    try {
      const _parentSku = extractValue(parentSku);
      if (!cartItemCount.value && qty < 0)
        throw new Error(t("qtyUnavailable").toString());
      isUpdatingQuantity.value = true;
      !cartItemCount.value ? await add({
        quantity: qty,
        sku: extractValue(sku),
        parentSku: _parentSku || "",
        entered_options: extractValue(options)?.entered_options,
        customizable_options: extractValue(options)?.customizable_options,
        bundle_options: extractValue(bundleOptions)
      }) : await updateItem(qty);
    } catch (e) {
      if (e.message.includes("requested qty is not available")) {
        warn("stock", t("outOfStock").toString());
      }
      throw e;
    } finally {
      isUpdatingQuantity.value = false;
    }
  }
  return {
    updateCartQty,
    isUpdatingQuantity
  };
}
export function useCartAttributes() {
  const { cartState, isFetchingCart, paymentFetching } = useStoredCart();
  const count = computed(() => {
    return cartState.value.itemCount;
  });
  return {
    ...toRefs(cartState.value),
    paymentFetching: computed(() => paymentFetching.value),
    cartSkus: computed(() => cartState.value.items.map(({ sku }) => sku)),
    isFetchingCart,
    count
  };
}
export function useCartDiscountedPrices() {
  const { cartState } = useStoredCart();
  const total = computed(() => {
    return cartState.value.subtotal - discount.value.reduce((a, b) => a + b?.value, 0);
  });
  const subtotal = computed(() => {
    return cartState.value.subtotal;
  });
  const discount = computed(() => {
    return cartState.value.discounts?.map((discount2) => ({
      label: discount2.label,
      value: discount2.value
    }));
  });
  return {
    total,
    subtotal,
    discount
  };
}
export function getCartItem(sku) {
  const { cartState } = useStoredCart();
  return cartState.value.items.find((i) => sku === i.sku);
}
export function getCartItems() {
  const { cartState } = useStoredCart();
  return cartState.value.items;
}
export function useCartItemCount(sku) {
  const { cartState } = useStoredCart();
  return {
    cartItemCount: computed(
      () => cartState.value.items.find((item) => item.sku === extractValue(sku))?.quantity
    ),
    cartItemTotalPrice: computed(
      () => cartState.value.items.find((item) => item.sku === extractValue(sku))?.totalPrice
    ),
    itemId: computed(
      () => cartState.value.items.find((item) => item.sku === extractValue(sku))?.id || ""
    ),
    item: computed(
      () => cartState.value.items.find((item) => item.sku === extractValue(sku)) || ""
    )
  };
}
function usePrepareCart() {
  const { cartState, patchCartState } = useStoredCart();
  const { execute } = useMutation(CreateCartDocument);
  const cartCookies = useCookie(CART);
  async function prepareCart() {
    if (cartState.value.cartId) {
      return;
    }
    const { data, error } = await execute();
    if (error) {
      throw new Error(error.message);
    }
    if (data?.response) {
      patchCartState({ cartId: data?.response || "" });
      cartCookies.value = data.response;
    }
  }
  return {
    prepareCart
  };
}
export function useApplyPromocode() {
  const { execute, isFetching } = useMutation(ApplyCouponToCartDocument);
  const cartCookies = useCookie(CART);
  const { cartState, patchCartState, mapCartState } = useStoredCart();
  const { resolveException } = useExceptions("cart");
  if (cartCookies.value) {
    cartState.value.cartId = cartCookies.value;
  }
  async function applyCoupon(code) {
    const { data, error } = await execute({
      input: { cart_id: cartState.value.cartId, coupon_code: code },
      ...resolveCartOptions("applyCouponToCart")
    });
    if (error && resolveException(error).level === "DANGER") {
      throw new Error(resolveException(error).message);
    }
    patchCartState(mapCartState(data?.response?.cart));
  }
  return {
    isFetching,
    applyCoupon
  };
}
export function useRemovePromocode() {
  const { execute, isFetching } = useMutation(RemoveCouponFromCartDocument);
  const { resolveException } = useExceptions("cart");
  const { cartState, patchCartState, mapCartState } = useStoredCart();
  async function removeCode() {
    const { data, error } = await execute({
      input: { cart_id: cartState.value.cartId },
      ...resolveCartOptions("removeCouponToCart")
    });
    if (error && resolveException(error).level === "DANGER") {
      throw new Error(resolveException(error).message);
    }
    patchCartState(mapCartState(data?.response?.cart));
    return { data, error };
  }
  return {
    removeCode,
    isFetching
  };
}
export function useApplyRewardPoints() {
  const { execute, isFetching } = useMutation(ApplyRewardPointsToCartDocument);
  const { resolveException } = useExceptions("cart");
  const { cartState, patchCartState, mapCartState } = useStoredCart();
  async function applyRewardPoints() {
    const { data, error } = await execute({
      cartId: cartState.value.cartId,
      ...resolveCartOptions("applyRewardToCart")
    });
    if (error && resolveException(error).level === "DANGER") {
      throw new Error(resolveException(error).message);
    }
    patchCartState(mapCartState(data?.response?.cart));
    return {
      data,
      error
    };
  }
  return {
    isFetching,
    applyRewardPoints
  };
}
export function useRemoveRewardPoints() {
  const { execute, isFetching } = useMutation(
    RemoveRewardPointsFromCartDocument.toString()
  );
  const { resolveException } = useExceptions("cart");
  const { cartState, patchCartState, mapCartState } = useStoredCart();
  async function removeRewardPoints() {
    const { data, error } = await execute({
      cartId: cartState.value.cartId,
      ...resolveCartOptions("removeRewardFromCard")
    });
    if (error && resolveException(error).level === "DANGER") {
      throw new Error(resolveException(error).message);
    }
    patchCartState(mapCartState(data?.response?.cart));
    return {
      data,
      error
    };
  }
  return {
    isFetching,
    removeRewardPoints
  };
}
export function usePaymentMethods(options = computed(() => ({
  withIframeEnabled: false,
  withIframeQueryParam: true,
  cached: "cache-first"
}))) {
  if (options.value.withIframeEnabled && !options.value.withIframeQueryParam) {
    throw new Error(
      "[PaymentMethods:Parameters] withIframeEnabled requires withIframeQueryParam to be enabled"
    );
  }
  const { isLoggedIn } = useAuth();
  const { isPageLoading } = useSsrCoreApp();
  const { cartState, patchCartState, mapCartState, paymentFetching } = useStoredCart();
  const route = useRoute();
  const {
    execute: executeGuestPaymentMethods,
    data: guestPaymentMethods,
    error: guestPaymentError,
    isFetching: isFetchingGuestPaymentMethod
  } = useQuery({
    query: GuestPaymentMethodsDocument.toString(),
    variables: {
      cartId: cartState.value.cartId
    },
    fetchOnMount: false,
    cachePolicy: options.value.cached
  });
  const {
    execute: executeCustomerPaymentMethods,
    data: customerPaymentMethods,
    error: customerPaymentError,
    isFetching: isFetchingCustomerPaymentMethods
  } = useQuery({
    query: PaymentMethodsDocument.toString(),
    fetchOnMount: false,
    cachePolicy: options.value.cached
  });
  const { execute, isFetching: isSettingPaymentMethod } = useMutation(
    SetPaymentMethodOnCartDocument?.toString()
  );
  const {
    execute: executeFramelessPaymentMethod,
    isFetching: isSettingFramelessPaymentMethod
  } = useMutation(SetFramelessPaymentMethodOnCartDocument);
  async function setPaymentMethod(code) {
    const params = {
      input: {
        cart_id: cartState.value.cartId,
        payment_method: {
          code
        }
      },
      ...resolveCartOptions("setPaymentMethod"),
      ...resolveCartRouteOptions("setPaymentMethod", route.name)
    };
    if (!options.value.withIframeQueryParam) {
      const { data: data2 } = await executeFramelessPaymentMethod(params);
      patchCartState(mapCartState(data2?.response?.cart));
      return;
    }
    const { data } = await execute({
      ...params,
      withExternalParameters: options.value.withIframeEnabled
    });
    patchCartState(mapCartState(data?.response?.cart));
    return data?.response;
  }
  const paymentMethods = computed(
    () => customerPaymentMethods.value?.cart?.paymentMethods || guestPaymentMethods.value?.cart?.paymentMethods || []
  );
  watch(
    isSettingPaymentMethod,
    (value) => {
      paymentFetching.value = value;
    },
    {
      immediate: true
    }
  );
  onMounted(async () => {
    while (isPageLoading.value) {
      await sleep(200);
    }
    if (isLoggedIn.value) {
      await executeCustomerPaymentMethods();
      return;
    }
    await executeGuestPaymentMethods();
  });
  const isFetchingPaymentMethod = computed(
    () => isFetchingCustomerPaymentMethods.value || isFetchingGuestPaymentMethod.value || isSettingFramelessPaymentMethod.value
  );
  return {
    paymentMethods,
    error: computed(
      () => customerPaymentError.value || guestPaymentError.value
    ),
    isFetching: computed(
      () => isPageLoading.value || isFetchingPaymentMethod.value || isSettingPaymentMethod.value || isSettingFramelessPaymentMethod.value
    ),
    setPaymentMethod
  };
}
export function useShippingMethods() {
  const { execute, isFetching } = useMutation(SetShippingMethodDocument);
  const { cartState, patchCartState, mapCartState } = useStoredCart();
  const route = useRoute();
  const shippingMethods = computed(() => {
    return uniqBy(
      toNonNullable(cartState.value.address?.available_shipping_methods).map(
        mapShippingMethod
      ) || [],
      "method_code"
    );
  });
  async function setShippingMethod(input) {
    const { data, error } = await execute({
      input: {
        cart_id: cartState.value.cartId,
        shipping_methods: [input]
      },
      ...resolveCartOptions("setShippingMethod"),
      ...resolveCartRouteOptions("setShippingMethod", route.name)
    });
    if (data?.response?.cart) {
      patchCartState(mapCartState(data?.response?.cart));
    }
    if (error) {
      throw new Error(error.message);
    }
    return {
      data,
      error
    };
  }
  return {
    setShippingMethod,
    shippingMethods,
    isFetching
  };
}
export function useSetShippingAddressOnCart() {
  const { cartState } = useStoredCart();
  const { execute, isFetching } = useMutation(SetShippingAddressDocument);
  const route = useRoute();
  async function setShippingAddressOnCart(address, storeCode) {
    const { error } = await execute({
      input: {
        cart_id: cartState.value.cartId,
        shipping_addresses: [
          isNumber(address) ? { customer_address_id: address } : {
            address,
            ...storeCode && { pickup_location_code: storeCode }
          }
        ]
      },
      ...resolveCartOptions("setCartAddress"),
      ...resolveCartRouteOptions("setCartAddress", route.name)
    });
    if (error) {
      throw new Error(error.message);
    }
  }
  return {
    setShippingAddressOnCart,
    isFetching
  };
}
export function useSetGuestEmailOnCart() {
  const { execute } = useMutation(SetGuestEmailOnCartDocument);
  const { cartState } = useStoredCart();
  async function setGuestEmailOnCart(email) {
    const { error, data } = await execute({
      cartId: cartState.value.cartId,
      email
    });
    if (error) {
      throw new Error(error.message);
    }
    return data;
  }
  return {
    setGuestEmailOnCart
  };
}
export function useSetBillingAddressOnCart() {
  const { execute, isFetching } = useMutation(SetBillingAddressDocument);
  const route = useRoute();
  const { cartState } = useStoredCart();
  async function setBillingAddressOnCart() {
    const { error, data } = await execute({
      input: {
        cart_id: cartState.value.cartId,
        billing_address: {
          same_as_shipping: true
        }
      },
      ...resolveCartOptions("setCartAddress"),
      ...resolveCartRouteOptions("setCartAddress", route.name)
    });
    if (error) {
      throw new Error(error.message);
    }
    return data;
  }
  return {
    setBillingAddressOnCart,
    isFetching
  };
}
export function useSetCartAddress() {
  const { setShippingAddressOnCart, isFetching: shippingAddressLoading } = useSetShippingAddressOnCart();
  const { setBillingAddressOnCart, isFetching: billingAddressLoading } = useSetBillingAddressOnCart();
  const { patchCartState, mapCartState } = useStoredCart();
  async function _setCartAddressById(addressId) {
    await setShippingAddressOnCart(addressId);
    const data = await setBillingAddressOnCart();
    if (data?.response?.cart) {
      patchCartState(mapCartState(data?.response?.cart));
    }
    return data?.response;
  }
  async function _setCartAddress(address, storeCode) {
    await setShippingAddressOnCart(address, storeCode);
    const data = await setBillingAddressOnCart();
    if (data?.response?.cart) {
      patchCartState(mapCartState(data?.response?.cart));
    }
    return data?.response;
  }
  async function setCartAddress(address, storeCode) {
    if (isNumber(address)) {
      await _setCartAddressById(address);
      return;
    }
    await _setCartAddress(address, storeCode);
  }
  return {
    setCartAddress,
    addressLoading: computed(
      () => shippingAddressLoading.value || billingAddressLoading.value
    )
  };
}
export function useSetCartStorePicker() {
  const { execute, isFetching } = useMutation(
    SetShippingLocationOnCartDocument
  );
  const { resolveException } = useExceptions("cart");
  const { cartState, patchCartState, mapCartState } = useStoredCart();
  async function setCartStorePicker(id) {
    const { data, error } = await execute({
      cartId: cartState.value.cartId,
      storeId: Number(id)
    });
    if (error && resolveException(error)?.level === "DANGER") {
      throw resolveException(error)?.message;
    }
    if (data?.response?.cart) {
      patchCartState(mapCartState(data?.response?.cart));
    }
  }
  return {
    setCartStorePicker,
    isFetching
  };
}
export function useEwalletPaymentMethods() {
  const { cartState, patchCartState, paymentFetching } = useStoredCart();
  const { execute, isFetching: isSettingPaymentMethod } = useMutation(
    SetWalletPaymentMethodDocument
  );
  async function setPaymentMethod(phoneNumber) {
    const { data } = await execute({
      cartId: cartState.value.cartId,
      phoneNumber
    });
    patchCartState({ iframeUrl: data?.response?.cart?.acceptIframeUrl });
    return data?.response;
  }
  watch(
    isSettingPaymentMethod,
    (value) => {
      paymentFetching.value = value;
    },
    {
      immediate: true
    }
  );
  return {
    isFetching: computed(() => isSettingPaymentMethod.value),
    setPaymentMethod
  };
}
export function useCartItemCustomizationOption(id) {
  const { cartState, patchCartState, mapCartState } = useStoredCart();
  const { execute: executeAddCustomOption } = useMutation(
    AddCustomOptionsToCartItemsDocument
  );
  const { execute: executeUpdateCustomOption } = useMutation(
    UpdateCartItemsCustomOptionsDocument
  );
  const { execute: executeRemoveCustomOption } = useMutation(
    RemoveCartItemsCustomOptionsDocument
  );
  const route = useRoute();
  async function addCustomizationOption(optionId, message) {
    const idValue = id.value ?? id;
    const result = await executeAddCustomOption({
      cartId: cartState.value.cartId,
      items: [
        {
          cart_item_uid: idValue,
          customizable_options: [
            {
              value_string: message,
              id: optionId
            }
          ]
        }
      ]
    });
    if (result.data?.response) {
      patchCartState(mapCartState(result?.data?.response.cart));
    }
  }
  async function updateCustomizationOption(optionId, message) {
    const idValue = id.value ?? id;
    const result = await executeUpdateCustomOption({
      cartId: cartState.value.cartId,
      items: [
        {
          cart_item_uid: idValue,
          customizable_options: [
            {
              value_string: message,
              id: optionId
            }
          ]
        }
      ],
      ...resolveCartOptions("UpdateCartItemsCustomOptions"),
      ...resolveCartRouteOptions("UpdateCartItemsCustomOptions", route.name)
    });
    if (result.data?.response) {
      patchCartState(mapCartState(result?.data?.response.cart));
    }
  }
  async function removeCustomizationOption(optionId) {
    const idValue = id.value ?? id;
    const result = await executeRemoveCustomOption({
      cartId: cartState.value.cartId,
      items: [
        {
          cart_item_uid: idValue,
          customizable_options: [
            {
              id: optionId,
              value_string: "N/A"
            }
          ]
        }
      ],
      ...resolveCartOptions("RemoveCartItemsCustomOptions"),
      ...resolveCartRouteOptions("RemoveCartItemsCustomOptions", route.name)
    });
    if (result.data?.response) {
      patchCartState(mapCartState(result?.data?.response.cart));
    }
  }
  return {
    updateCustomizationOption,
    addCustomizationOption,
    removeCustomizationOption
  };
}
export function useCartErrors() {
  const { error: errorAlert } = useAlerts();
  const { t } = useI18n();
  function cartError(error) {
    switch (error.code) {
      case "INSUFFICIENT_STOCK":
        errorAlert(t("stockError").toString(), error.message);
        break;
      case "UNDEFINED":
        errorAlert(t("stockError").toString(), error.message);
        break;
      default:
        errorAlert(t("stockError").toString(), error.message);
        break;
    }
  }
  return {
    cartError
  };
}
export function useIsGuestCart() {
  const { isPageLoading } = useSsrCoreApp();
  const { user } = useAuth();
  const cartCookie = useCookie(CART);
  return {
    isGuestCart: computed(
      () => !isPageLoading.value && cartCookie.value && !user.value
    )
  };
}
export function useAddNoteToCart() {
  const { cartState } = useStoredCart();
  const { execute } = useMutation(AddNoteToCartDocument);
  async function addNoteToCart(note) {
    const { data, error } = await execute({
      cartId: cartState.value.cartId,
      note
    });
    if (error) {
      throw new Error(error.message);
    }
    return {
      data,
      error
    };
  }
  return {
    addNoteToCart
  };
}
export function useSetOrderSourceOnCart() {
  const { cartState } = useStoredCart();
  const {
    public: { commerce }
  } = useRuntimeConfig();
  const { execute } = useMutation(SetOrderSourceOnCartDocument);
  async function addOrderSourceOnCart() {
    const { data, error } = await execute({
      input: {
        cart_id: cartState.value.cartId,
        order_source: commerce?.orderSource
      }
    });
    if (error) {
      throw new Error(error.message);
    }
    return {
      data,
      error
    };
  }
  return {
    addOrderSourceOnCart
  };
}
export const GLOBAL_CONFIGURATION = {
  fetchItems: true
};
export function mapShippingMethod(shippingMethod) {
  return {
    ...shippingMethod,
    name: shippingMethod?.method_title ?? "",
    isActive: !shippingMethod?.is_after_cutoff_time,
    minDays: shippingMethod?.min_days ?? null,
    maxDays: shippingMethod?.max_days ?? null,
    cutoffTime: shippingMethod?.cutoff_time ?? null,
    minSubTotalFreeShipping: shippingMethod?.min_subtotal_for_free_shipping ?? null
  };
}
